import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchScanImages, fetchFullImageObjectUrl } from '../api';
import type { JobImages } from '../types';
import { createLogger } from '../../utils/logger';
import { SyncedPanoramaPair } from './SyncedPanoramaPair';
import { InteriorScanDebugModal } from './InteriorScanDebugModal';

const log = createLogger('InteriorImagesModal');

interface Props {
  jobId: string;
  scanId: string;
  onClose: () => void;
}

type Variant = 'enhanced' | 'original';

interface SceneImage {
  scene: string;
  url: string;
}

// Mirrors lightning's image-download flow: fetch the URL, save the blob
// with a stable name, fall back to opening in a new tab if the fetch
// somehow fails (e.g. CORS on a presigned URL).
function inferExtension(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\.([a-zA-Z0-9]{1,5})$/);
    if (match) return match[1].toLowerCase();
  } catch {
    // ignore parse errors and fall back below
  }
  return 'png';
}

function buildFilename(image: SceneImage, scanId: string, variant: Variant): string {
  const ext = inferExtension(image.url);
  return `${scanId}_${variant}_${image.scene}.${ext}`;
}

async function downloadImage(
  image: SceneImage,
  scanId: string,
  variant: Variant,
): Promise<void> {
  const filename = buildFilename(image, scanId, variant);
  try {
    const response = await fetch(image.url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    log.debug(`Downloaded ${filename}`);
  } catch (err) {
    log.error(`Failed to download ${filename}:`, err);
    window.open(image.url, '_blank', 'noopener,noreferrer');
  }
}

interface ImageCardProps {
  image: SceneImage;
  onOpen: (image: SceneImage) => void;
  onDownload: (image: SceneImage) => void;
}

function ImageCard({ image, onOpen, onDownload }: ImageCardProps) {
  const [errored, setErrored] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(image);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(image)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(image);
        }
      }}
      className="group block bg-surface-50 rounded-lg overflow-hidden border border-surface-200 hover:border-primary-400 hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
      title="Click to enlarge"
    >
      <div className="aspect-[4/3] bg-surface-100 flex items-center justify-center overflow-hidden">
        {errored ? (
          <div className="text-surface-400 text-xs flex flex-col items-center gap-1 p-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Failed to load</span>
          </div>
        ) : (
          <img
            src={image.url}
            alt={image.scene}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={() => setErrored(true)}
          />
        )}
      </div>
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-surface-700 capitalize truncate">
          {image.scene.replace(/_/g, ' ')}
        </span>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="p-1.5 rounded-md text-surface-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Download image"
          aria-label="Download image"
        >
          {downloading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

interface LightboxProps {
  images: SceneImage[];
  index: number;
  variant: Variant;
  onClose: () => void;
  onIndexChange: (next: number) => void;
  /** Toggle between enhanced/original. Keeps the same scene in view. */
  onVariantToggle: () => void;
  onDownload: (image: SceneImage) => Promise<void>;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: Transform = { scale: 1, tx: 0, ty: 0 };

/**
 * Full-screen image lightbox — direct port of lightning's `Lightbox`
 * sub-component in `web/src/components/ImagesModal.tsx`. Click to open,
 * wheel to zoom, double-click to toggle 1×/2×, drag to pan when zoomed,
 * ←/→ to navigate between scenes, ↑/↓ to toggle the variant
 * (enhanced ↔ original) for the *current* scene — interior-specific
 * affordance since the two variants share scene names — and ESC
 * bubbles up to the parent modal.
 */
function Lightbox({
  images,
  index,
  variant,
  onClose,
  onIndexChange,
  onVariantToggle,
  onDownload,
}: LightboxProps) {
  const image = images[index];
  const total = images.length;

  const [errored, setErrored] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [transform, setTransform] = useState<Transform>(IDENTITY);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseTx: number;
    baseTy: number;
    pointerId: number;
  } | null>(null);

  useEffect(() => {
    setErrored(false);
    setTransform(IDENTITY);
  }, [image?.url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onIndexChange((index - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onIndexChange((index + 1) % total);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Only two variants — both keys just toggle. The parent keeps
        // the lightbox on the same scene name across the swap; if that
        // scene doesn't exist in the other variant the parent closes
        // the lightbox (defensive — shouldn't happen in practice since
        // `enhanced`/`original` come from the same cropper response).
        e.preventDefault();
        onVariantToggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onIndexChange, onVariantToggle]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setTransform((prev) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        if (nextScale === MIN_SCALE) return IDENTITY;
        const ratio = nextScale / prev.scale;
        return {
          scale: nextScale,
          tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
          ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(image);
    } finally {
      setDownloading(false);
    }
  };

  const goPrev = () => onIndexChange((index - 1 + total) % total);
  const goNext = () => onIndexChange((index + 1) % total);

  const onPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (transform.scale <= MIN_SCALE) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: transform.tx,
      baseTy: transform.ty,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    e.preventDefault();
    setTransform((prev) => ({
      ...prev,
      tx: d.baseTx + (e.clientX - d.startX),
      ty: d.baseTy + (e.clientY - d.startY),
    }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (d && e.pointerId === d.pointerId) {
      dragRef.current = null;
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setTransform((prev) => {
      if (prev.scale > MIN_SCALE) return IDENTITY;
      const nextScale = 2;
      const ratio = nextScale / prev.scale;
      return {
        scale: nextScale,
        tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
        ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
      };
    });
  };

  const resetZoom = () => setTransform(IDENTITY);

  const isZoomed = transform.scale > MIN_SCALE;
  const cursorClass = isZoomed
    ? dragRef.current
      ? 'cursor-grabbing'
      : 'cursor-grab'
    : 'cursor-zoom-in';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center select-none">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />

      <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
        {isZoomed && (
          <button
            type="button"
            onClick={resetZoom}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium backdrop-blur transition-colors"
            title="Reset zoom"
            aria-label="Reset zoom"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9l6 6m0-6l-6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{Math.round(transform.scale * 100)}%</span>
          </button>
        )}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium backdrop-blur disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Download image"
          aria-label="Download image"
        >
          {downloading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
          )}
          <span>{downloading ? 'Saving...' : 'Download'}</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors"
          aria-label="Close preview"
          title="Close (Esc)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="absolute top-4 left-4 flex items-center gap-2 z-20">
        <span
          className={`px-3 py-2 rounded-lg text-sm font-medium backdrop-blur ${
            variant === 'enhanced'
              ? 'bg-indigo-500/30 text-white'
              : 'bg-white/10 text-white'
          }`}
          title="↑/↓ to toggle variant"
        >
          {variant === 'enhanced' ? 'Enhanced' : 'Original'}
        </span>
        {total > 1 && (
          <span className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm font-medium backdrop-blur">
            {index + 1} / {total}
          </span>
        )}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors z-20"
            aria-label="Previous image"
            title="Previous (←)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors z-20"
            aria-label="Next image"
            title="Next (→)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}


      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={onDoubleClick}
      >
        {errored ? (
          <div className="text-white/80 text-sm flex flex-col items-center gap-2 p-8">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Failed to load image</span>
          </div>
        ) : (
          <img
            key={image.url}
            src={image.url}
            alt={image.scene}
            draggable={false}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onError={() => setErrored(true)}
            className={`max-w-[92vw] max-h-[85vh] object-contain rounded-lg shadow-2xl ${cursorClass}`}
            style={{
              transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
              transformOrigin: 'center center',
              transition: dragRef.current ? 'none' : 'transform 80ms ease-out',
              willChange: 'transform',
            }}
          />
        )}
      </div>

      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-2 py-1 rounded-lg bg-white/10 text-white text-sm backdrop-blur z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onVariantToggle();
          }}
          className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
          aria-label={`Switch to ${variant === 'enhanced' ? 'original' : 'enhanced'} variant`}
          title={`Switch to ${variant === 'enhanced' ? 'original' : 'enhanced'} (↑)`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <span className="font-medium capitalize px-2">
          {image.scene.replace(/_/g, ' ')}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onVariantToggle();
          }}
          className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
          aria-label={`Switch to ${variant === 'enhanced' ? 'original' : 'enhanced'} variant`}
          title={`Switch to ${variant === 'enhanced' ? 'original' : 'enhanced'} (↓)`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Modal that shows the per-scene crops produced by the apollo cropper.
 *
 * Mirrors lightning's `ImagesModal` UX: clicking an image opens an
 * in-modal `Lightbox` overlay (zoom / pan / arrow-key navigation /
 * download) instead of a raw new-tab navigation.
 *
 * Interior-specific bits kept on top of that base:
 * - Enhanced / Original variant toggle (interior emits both per scene).
 * - ODD-failure banner + per-scene failure reasons.
 */
export function ImagesModal({ jobId, scanId, onClose }: Props) {
  const [data, setData] = useState<JobImages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [variant, setVariant] = useState<Variant>('enhanced');
  // Track the scene *name* in preview (not the index): keeps the
  // lightbox on the same scene when the user flips the variant via
  // ↑/↓, since the sorted index can differ between variant arrays
  // (and remains stable if a scene is added/removed).
  const [previewScene, setPreviewScene] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  // Object URLs for the full 360 pair. Fetched as authenticated blobs
  // via the same-origin proxy (the /images presigned S3 URLs can't be
  // read by a WebGL texture — cross-origin, no CORS).
  const [fullImageUrls, setFullImageUrls] = useState<{ original: string; enhanced: string } | null>(null);

  const hasFullImage = Boolean(data?.fullImage?.original && data?.fullImage?.enhanced);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchScanImages(jobId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Default to original if enhanced is empty but original isn't.
        if (Object.keys(d.enhancedCrops).length === 0 && Object.keys(d.originalCrops).length > 0) {
          setVariant('original');
        }
        setError(null);
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!hasFullImage) {
      setFullImageUrls(null);
      return;
    }
    let cancelled = false;
    const created: string[] = [];
    Promise.all([
      fetchFullImageObjectUrl(jobId, 'original'),
      fetchFullImageObjectUrl(jobId, 'enhanced'),
    ])
      .then(([original, enhanced]) => {
        created.push(original, enhanced);
        if (cancelled) {
          URL.revokeObjectURL(original);
          URL.revokeObjectURL(enhanced);
          return;
        }
        setFullImageUrls({ original, enhanced });
      })
      .catch((ex) => {
        if (!cancelled) log.error('Failed to load full 360 images:', ex);
      });
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
      setFullImageUrls(null);
    };
  }, [jobId, hasFullImage]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // While the debug panel is open it owns Escape (it renders on top
      // and closes itself); don't also tear down this modal underneath.
      if (showDebug) return;
      if (previewScene !== null) {
        setPreviewScene(null);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, previewScene, showDebug]);

  const images = useMemo<SceneImage[]>(() => {
    if (!data) return [];
    const source = variant === 'enhanced' ? data.enhancedCrops : data.originalCrops;
    return Object.entries(source)
      .map(([scene, url]) => ({ scene, url }))
      .sort((a, b) => a.scene.localeCompare(b.scene));
  }, [data, variant]);

  // If the variant flips to one that doesn't contain the previewed
  // scene (rare — both variants come from the same cropper response),
  // close the lightbox instead of showing a missing image. Without
  // this guard the lightbox would render `images[-1]` and crash.
  useEffect(() => {
    if (previewScene === null) return;
    if (images.findIndex((it) => it.scene === previewScene) === -1) {
      setPreviewScene(null);
    }
  }, [previewScene, images]);

  const handleDownload = (image: SceneImage) => downloadImage(image, scanId, variant);

  const handleDownloadAll = async () => {
    if (bulkProgress || images.length === 0) return;
    setBulkProgress({ current: 0, total: images.length });
    for (let i = 0; i < images.length; i++) {
      setBulkProgress({ current: i + 1, total: images.length });
      await downloadImage(images[i], scanId, variant);
    }
    setBulkProgress(null);
  };

  const canDownloadAll = !!data && !loading && !error && images.length > 0;
  const openPreview = (image: SceneImage) => setPreviewScene(image.scene);

  // Derived: the position of the previewed scene in the current
  // variant's array. ``-1`` means the scene isn't in this variant
  // (the effect above will close the lightbox on the next render).
  const previewIndex =
    previewScene === null ? -1 : images.findIndex((it) => it.scene === previewScene);

  const handleSceneIndexChange = (next: number) => {
    const target = images[next];
    if (target) setPreviewScene(target.scene);
  };

  const handleVariantToggle = () => {
    setVariant((v) => (v === 'enhanced' ? 'original' : 'enhanced'));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between flex-wrap gap-2 border-b border-slate-200 px-4 sm:px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Interior crops</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Scan <span className="font-mono">{scanId}</span>
              {data?.oddPassed === false && (
                <span className="ml-2 inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-medium">
                  ODD failed
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setVariant('enhanced')}
                className={`px-3 py-1 transition-colors ${
                  variant === 'enhanced'
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Enhanced
              </button>
              <button
                type="button"
                onClick={() => setVariant('original')}
                className={`px-3 py-1 border-l border-slate-200 transition-colors ${
                  variant === 'original'
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Original
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowDebug(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-surface-700 bg-surface-100 hover:bg-surface-200 rounded-lg transition-colors"
              title="Open interior debug panel"
              aria-label="Open interior debug panel"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span>Debug panel</span>
            </button>
            {canDownloadAll && (
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={!!bulkProgress}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Download all images"
                aria-label="Download all images"
              >
                {bulkProgress ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                )}
                <span>
                  {bulkProgress
                    ? `Downloading ${bulkProgress.current}/${bulkProgress.total}...`
                    : `Download all (${images.length})`}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && <div className="text-sm text-slate-500">Loading crops…</div>}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          {!loading && !error && images.length === 0 && (
            <div className="text-sm text-slate-500">
              No {variant} crops available for this job.
              {data?.errorScenes && Object.keys(data.errorScenes).length > 0 && (
                <> All scenes failed — see the list below.</>
              )}
            </div>
          )}
          {hasFullImage && (
            <section className="mb-6">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-900">Full 360 image</h3>
                <span className="text-xs text-slate-500">drag to look around, scroll to zoom (synced)</span>
              </div>
              {fullImageUrls ? (
                <SyncedPanoramaPair
                  originalUrl={fullImageUrls.original}
                  enhancedUrl={fullImageUrls.enhanced}
                />
              ) : (
                <div className="aspect-[16/9] md:aspect-[32/9] w-full rounded-lg border border-surface-200 bg-surface-100 flex items-center justify-center text-sm text-slate-400">
                  Loading 360 view…
                </div>
              )}
            </section>
          )}
          {images.length > 0 && (
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Crops</h3>
          )}
          {images.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <ImageCard
                  key={image.scene}
                  image={image}
                  onOpen={openPreview}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          )}
          {data?.errorScenes && Object.keys(data.errorScenes).length > 0 && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Failed scenes</h3>
              <ul className="space-y-1 text-xs text-amber-900">
                {Object.entries(data.errorScenes).map(([scene, reason]) => (
                  <li key={scene}>
                    <span className="font-mono">{scene}</span>: {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {images.length > 0 && (
          <div className="px-4 sm:px-6 py-3 border-t border-surface-200 bg-surface-50 text-xs text-surface-500">
            Click an image to enlarge (←/→ to change scene, ↑/↓ to swap enhanced/original, scroll to zoom, drag to pan), or use the download buttons to save.
          </div>
        )}
      </div>

      {previewIndex >= 0 && images[previewIndex] && (
        <Lightbox
          images={images}
          index={previewIndex}
          variant={variant}
          onClose={() => setPreviewScene(null)}
          onIndexChange={handleSceneIndexChange}
          onVariantToggle={handleVariantToggle}
          onDownload={handleDownload}
        />
      )}

      <InteriorScanDebugModal
        isOpen={showDebug}
        jobId={jobId}
        scanId={scanId}
        onClose={() => setShowDebug(false)}
      />
    </div>
  );
}
